import { ICompileService, isArray } from "angular";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetService,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  RecordGridViewService,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
  vsServiceName as RecordGridViewServiceName,
} from "../../../records/views/recordGridView";
import { RecordDataSource } from "../../../records/grids";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";

import { machineConfigurationObjectName } from "../../shared/names";
export { machineConfigurationObjectName as vsObjectName } from "../../shared/names";

import { vsServiceName as MachineDetailTabsServiceName } from "../../shared/components/views/machines/detail/tabs";

import { vsObjectName as machineConfigurationTypeName } from "./machineConfigurationType";
import {
  FormViewInstanceConfig,
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../../records/forms/formView";
import { Machine } from "../models/machine";
import { ServiceResponse } from "../../../records/server/query";
import {
  AuthorizedUiElementConfig,
  AuthorizedUiElementService,
} from "../../../ui/authorizedUiElement";
import {
  IExpressionFunction,
  IFieldArray,
  IFieldRuntimeObject,
  ITemplateScope,
  IWatcher,
} from "AngularFormly";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import { get, set } from "lodash";
import { LookupManagementService } from "../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as MachineLookupManagementServiceName } from "../../shared/lookups";
import * as lookupNames from "../configs/lookup";
import { vsObjectName as epromObjectName } from "../configs/machineEpromConfigurations";
import { vsObjectName as epromLabelObjectName } from "../configs/lookup/machineEpromConfigurationLabel";
import { moneyValueGetterFactory } from "../../../records/grids/cells/valueGetters";

export const machineConfigViewDefaultSort =
  "PropertyName,Status,Location,SerialNumber,-Date,Order";

machineConfigurationConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
  FormViewServiceProviderName,
];

export interface MachineEditFormViewConfig extends FormViewInstanceConfig {
  machineFieldsEditable?: boolean;
}

export function machineConfigurationConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider,
  formViewServiceProvider: FormViewServiceProvider
) {
  registerGrid(
    recordGridViewServiceProvider,
    columnDefinitionSetServiceProvider
  );
  registerForm(formViewServiceProvider);
}

function registerGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(
    machineConfigurationObjectName,
    [
      ServerUtilityName,
      GridSettingsServiceName,
      (
        serverUtility: ServerUtility,
        gridSettingsService: GridSettingsService
      ) => {
        return {
          columnDefinitionSet: machineConfigurationObjectName,
          defaultSort: machineConfigViewDefaultSort,
          gridDataSource: new RecordDataSource(
            serverUtility,
            "/api/machines/MachineConfiguration"
          ),
          gridSettings: gridSettingsService.getGridSettings(
            Symbol(machineConfigurationObjectName)
          ),
          nameSingular: "Machine Configuration",
          namePlural: "Machines Configurations",
          showAddButton: false,
          showAddButtonPermission: "machineEdit",
        };
      },
    ]
  );

  columnDefinitionSetServiceProvider.addColumnDefinitionSet(
    machineConfigurationObjectName,
    [
      ColumnDefinitionServiceName,
      ColumnDefinitionSetServiceName,
      (
        columnDefinitionService: ColumnDefinitionService,
        columnDefinitionSetService: ColumnDefinitionSetService
      ) => {
        const machineConfigurationTypeColumns =
          columnDefinitionSetService.getColumnDefinitionSet(
            machineConfigurationTypeName
          ) ?? [];

        const columns = [
          columnDefinitionService.getIdentityColDef({
            field: "Id",
            hide: true,
          }),
          columnDefinitionService.getIdentityColDef({
            field: "MachineId",
            hide: true,
          }),
          columnDefinitionService.getIdentityColDef({
            field: "MachineConfigurationId",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineManufacturerName",
            header: "Manufacturer",
          }),
          columnDefinitionService.getStringColDef({
            field: "SerialNumber",
          }),
          columnDefinitionService.getOptionColDef({
            field: "Status",
            options: [
              {
                display: "Floor",
                value: "Floor",
              },
              {
                display: "Warehoused",
                value: "Warehoused",
              },
              {
                display: "Decommissioned",
                value: "Decommissioned",
              },
            ],
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineWarehouseTypeName",
            header: "Warehouse Type",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "WarehouseLocation",
            header: "Warehouse Location",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineConversionTypeName",
            header: "Conversion Type",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineDecommissionTypeName",
            header: "Decommission Type",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineCabinetTypeName",
            header: "Cabinet Type",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineModelTypeName",
            header: "Model Type",
          }),

          columnDefinitionService.getStringColDef({
            field: "SlotNumber",
          }),
          columnDefinitionService.getBooleanColDef({
            field: "EnabledFeatures",
            header: "Enabled Features",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineFeatureOptionsName",
            header: "Feature Options",
          }),
          columnDefinitionService.getBooleanColDef({
            field: "LcdTopper",
            header: "LCD Topper",
          }),
          columnDefinitionService.getBooleanColDef({
            field: "Flagged",
            header: "Flagged?",
          }),
          columnDefinitionService.getStringColDef({
            field: "Location",
          }),
          columnDefinitionService.getStringColDef({
            field: "Section",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Bank",
            hide: true,
          }),
          
          columnDefinitionService.getDateColDef({
            field: "Date",
          }),
          columnDefinitionService.getStringColDef({
            field: "BankSize",
          }),
          columnDefinitionService.getStringColDef({
            field: "Position",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineBankTypeName",
            header: "Bank Type",
          }),
          columnDefinitionService.getStringColDef({
            field: "Notes",
          }),
          columnDefinitionService.getBooleanColDef({
            field: "CurrentConfiguration",
            header: "Current Configuration",
            hide: true,
          }),
          columnDefinitionService.getOptionColDef({
            field: "ChangeType",
            options: [
              {
                display: "New Machine",
                value: "NewMachine",
              },
              {
                display: "Conversion",
                value: "Conversion",
              },
              {
                display: "Decommission",
                value: "Decommission",
              },
              {
                display: "Move",
                value: "Move",
              },
            ],
            hide: true,
          }),

          columnDefinitionService.getNumberColDef({
            field: "FlatFee",
            header: "Flat Fee",
            hide: true,
            valueGetter: moneyValueGetterFactory(),
          }),
          /* TODO - add client specific machine and machine configuration columns here */
          ...machineConfigurationTypeColumns,
        ];

        return columns;
      },
    ]
  );
}

function registerForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(machineConfigurationObjectName, [
    "config",
    FormViewServiceName,
    MachineDetailTabsServiceName,
    FieldConfigurationHelperServiceName,
    MachineLookupManagementServiceName,
    RecordGridViewServiceName,
    (
      config: MachineEditFormViewConfig,
      formViewService: FormViewService,
      machineDetailTabsService: AuthorizedUiElementService<AuthorizedUiElementConfig>,
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      machineLookupManagementService: LookupManagementService,
      recordGridViewService: RecordGridViewService
    ) => {
      const epromGridConfiguration =
        recordGridViewService.getGridConfiguration(epromObjectName)!;

      return {
        fields: <IFieldArray>[
          fieldConfigurationHelperService.lookupField(
            machineLookupManagementService,
            {
              key: getConfigurationFieldKey("MachineConversionTypeId"),
              objectName: lookupNames.conversionTypeName,
              hideExpression: hideIfNotChangeType("Conversion"),
            }
          ),
          fieldConfigurationHelperService.lookupField(
            machineLookupManagementService,
            {
              key: getConfigurationFieldKey("MachineDecommissionTypeId"),
              objectName: lookupNames.decommissionTypeName,
              hideExpression: hideIfNotChangeType("Decommission"),
            }
          ),
          fieldConfigurationHelperService.lookupField(
            machineLookupManagementService,
            {
              key: getConfigurationFieldKey("MachineWarehouseTypeId"),
              objectName: lookupNames.warehouseTypeName,
              hideExpression: hideIfNotChangeType("Warehouse"),
            }
          ),
          fieldHelpers.headerField({
            headerText: "EGM Information",
            key: "EGMInformation",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  objectName: lookupNames.machineManufacturerName,
                  key: getMachineFieldKey("MachineManufacturerId"),
                  required: true,
                  label: "Manufacturer",
                  readonly: !config.machineFieldsEditable,
                }
              ),
              fieldHelpers.simpleStringField({
                key: getMachineFieldKey("SerialNumber"),
                readonly: !config.machineFieldsEditable,
                label: "Serial Number",
                required: true,
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("SlotNumber"),
                label: "Asset Number",
                expressionProperties: requireIfStatus("Floor"),
              }),
              
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getMachineFieldKey("MachineCabinetTypeId"),
                  objectName: lookupNames.cabinetTypeName,
                  readonly: !config.machineFieldsEditable,
                  required: true,
                }
              ),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getMachineFieldKey("MachineModelTypeId"),
                  objectName: lookupNames.modelTypeName,
                  readonly: !config.machineFieldsEditable,
                  required: true,
                }
              ),

              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("InstantBonus"),
                label: "Instant Bonus",
                defaultValue: false,
              }),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("MachineFeatureOptionsId"),
                  objectName: lookupNames.featureOptionsName,
                }
              ),
              
              
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("LcdTopper"),
                label: "LCD Topper",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getMachineFieldKey("Flagged"),
                label: "Flagged?",
                defaultValue: false,
                readonly: !config.machineFieldsEditable,
              }),
              {
                key: getConfigurationFieldKey("Status"),
                name: "Status",
                type: "select",
                templateOptions: {
                  label: "Status",
                  options: [
                    {
                      label: "Floor",
                      value: "Floor",
                    },
                    {
                      label: "Warehoused",
                      value: "Warehoused",
                    },
                    {
                      label: "Decommissioned",
                      value: "Decommissioned",
                    },
                  ],
                  required: true,
                  labelProp: "label",
                  valueProp: "value",
                },
              },
              fieldHelpers.simpleDateField({
                key: getConfigurationFieldKey("Date"),
                label: "Date",
                required: true,
              }),
            ],
            4
          ),

          fieldHelpers.headerField({
            headerText: "Location Data",
            key: "LocationData",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("Section"),
                expressionProperties: requireIfStatus("Floor"),
                hideExpression: hideIfNotStatus("Floor"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Floor",
                  getConfigurationFieldKey("Section")
                ),
                label: "Section",
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("Bank"),
                expressionProperties: requireIfStatus("Floor"),
                hideExpression: hideIfNotStatus("Floor"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Floor",
                  getConfigurationFieldKey("Bank")
                ),
                label: "Bank",
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("Position"),
                hideExpression: hideIfNotStatus("Floor"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Floor",
                  getConfigurationFieldKey("Position")
                ),
                label: "Position",
                expressionProperties: requireIfStatus("Floor"),
              }),
              
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("BankSize"),
                hideExpression: hideIfNotStatus("Floor"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Floor",
                  getConfigurationFieldKey("BankSize")
                ),
                label: "Bank Size",
              }),
              

              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("MachineBankTypeId"),
                  hideExpression: hideIfNotStatus("Floor"),
                  objectName: lookupNames.bankTypeName,
                  watcher: watchAndSetUndefinedIfNotStatus("Floor", [
                    getConfigurationFieldKey("MachineBankTypeId"),
                    getConfigurationFieldKey("MachineBankType"),
                  ]),
                }
              ),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("WarehouseLocation"),
                label: "Warehouse Location",
                hideExpression: hideIfNotStatus("Warehoused"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Warehoused",
                  getConfigurationFieldKey("WarehouseLocation")
                ),
              }),
            ],
            4
          ),

          fieldHelpers.headerField({
            headerText: "Game Configuration",
            key: "GameMathHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              {
                ...fieldConfigurationHelperService.selectAsyncField({
                  key: getConfigurationFieldKey("MachineConfigurationTypeId"),
                  label: "Type ID",
                  queryUrl: "/api/machines/MachineConfigurationType",
                  serverItemMapper: (model: any) => ({
                    Id: model.Id,
                    Name: model.MachineConfigurationTypeName,
                  }),
                  queryBuilder: (search, model) => ({
                    MachineConfigurationTypeNameContains: search,
                  }),
                  getDefaultItem: (model: any) =>
                    get(
                      model,
                      getConfigurationFieldKey("MachineConfigurationType")
                    ),
                }),
                expressionProperties: requireIfStatus("Floor"),
              },
            ],
            1
          ),
          fieldHelpers.headerField({
            headerText: "Eproms",
            key: "EpromsHeader",
          }),
          fieldConfigurationHelperService.repeaterField({
            key: getConfigurationFieldKey("Eproms"),
            allowAdd: true,
            allowAddBottom: true,
            displayNameSingular: epromGridConfiguration.nameSingular ?? "Eprom",
            displayNamePlural: epromGridConfiguration.namePlural ?? "Eproms",
            repeaterFields: fieldHelpers.columnLayout(
              [
                fieldConfigurationHelperService.lookupField(
                  machineLookupManagementService,
                  {
                    objectName: epromLabelObjectName,
                    key: "MachineEpromConfigurationLabelId",
                    label: "Label",
                  }
                ),
                fieldHelpers.simpleStringField({
                  key: "Eprom",
                  label: "EPROM",
                }),
                {
                  key: "copyDelete",
                  type: "recordRepeaterCopyDelete",
                  className: "col-xs-6",
                  templateOptions: {
                    label: " ",
                    allowMove: true,
                  },
                },
              ],
              4
            ),
            showHeader: false,
          }),
          

          fieldHelpers.headerField({
            headerText: "Lease Information",
            key: "LeaseInformation",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("MachineLeasedId"),
                  objectName: lookupNames.machineLeasedName,
                  required: true,
                }
              ),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("MachineLeaseCategoryId"),
                  objectName: lookupNames.machineLeaseCategoryName,
                }
              ),
              fieldHelpers.simpleNumberField({
                key: getConfigurationFieldKey("DailyFee"),
                label: "Daily Fee",
              }),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simplePercentageField({
                key: getConfigurationFieldKey("PerNetWin"),
                label: "% of Net Win",
              }),
              fieldHelpers.simplePercentageField({
                key: getConfigurationFieldKey("PerCoinIn"),
                label: "% of Coin In",
              }),
            ],
            4
          ),

          fieldHelpers.headerField({
            headerText: "Player Tracking & Limit",
            key: "LeaseInformation",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleNumberField({
                key: getConfigurationFieldKey("PrinterLimitOption"),
                label: "Printer Limit Option",
              }),
              fieldHelpers.simpleNumberField({
                key: getConfigurationFieldKey("CreditLimitOption"),
                label: "Credit Limit Option",
              }),
              fieldHelpers.simpleNumberField({
                key: getConfigurationFieldKey("TicketLimitOption"),
                label: "Ticket Limit Option",
              }),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("MachinePlayerTrackingTypeId"),
                  objectName: lookupNames.machinePlayerTrackingTypeName,
                }
              ),
            ],
            4
          ),

          fieldHelpers.headerField({
            headerText: "Date Information",
            key: "DateInformation",
          }),
          ...fieldHelpers.columnLayout(
            [
              
              fieldHelpers.simpleDateField({
                key: getMachineFieldKey("PurchaseDate"),
                label: "Purchase Date",
                readonly: !config.machineFieldsEditable,
              }),
              fieldHelpers.simpleDateField({
                key: getConfigurationFieldKey("StartDate"),
                label: "Go- Live Date",
              }),
              fieldHelpers.simpleDateField({
                key: getConfigurationFieldKey("ManufacturerDate"),
                label: "Manufacturer Date",
              }),
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "Compliance Files",
            key: "FileHeader",
          }),
          {
            key: getConfigurationFieldKey("Files"),
            type: "recordFileUpload",
            templateOptions: {
              label: "Compliance Files",
              allowMultiple: true,
              hideLabel: true,
              group: "Compliance",
            },
          },
          fieldHelpers.headerField({
            headerText: "Software Compliance Notices",
            key: "SoftwareComplianceHeader",
          }),
          {
            key: getConfigurationFieldKey("Files"),
            type: "recordFileUpload",
            templateOptions: {
              label: "Software Compliance Notices",
              allowMultiple: true,
              hideLabel: true,
              group: "SoftwareCompliance",
            },
          },
          fieldHelpers.headerField({
            headerText: "Internal Documentation",
            key: "InternalDocHeader",
          }),
          {
            key: getConfigurationFieldKey("Files"),
            type: "recordFileUpload",
            templateOptions: {
              label: "Internal Documentation",
              allowMultiple: true,
              hideLabel: true,
              group: "InternalDocumentation",
            },
          },
          fieldHelpers.headerField({
            headerText: "Machine PAR Sheets",
            key: "MachineParSheets",
          }),
          {
            key: getConfigurationFieldKey("Files"),
            type: "recordFileUpload",
            templateOptions: {
              label: "Machine PAR Sheets",
              allowMultiple: true,
              hideLabel: true,
              group: "MachineParSheets",
            },
          },
          fieldHelpers.headerField({
            headerText: "Notes",
            key: "NotesHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.textAreaField({
                key: getConfigurationFieldKey("Notes"),
                label: " ",
                rows: 5,
              }),
            ],
            1
          ),
        ],
        name: machineConfigurationObjectName,
        submitForm: (args) =>
          formViewService
            .submitForm<ServiceResponse<Machine>>({
              formObject: args.model,
              formViewController: args.formViewController,
              method: "POST",
              url: "/api/machines/Machine",
            })
            .then((result) =>
              machineDetailTabsService.navigateToChildByIndex(0, {
                machineId: result.Result.Id,
              })
            ),
        title: () => "Machine",
        showButtonsAtTop: true,
      };
    },
  ]);
}

function requireIfStatus(status: string): {
  [key: string]: IExpressionFunction;
} {
  return {
    "templateOptions.required": (_, __, scope) =>
      scope.model?.Configuration.Status === status,
  };
}

function hideIfNotStatus(status: string) {
  return (_: any, __: any, scope: ITemplateScope) =>
    get(scope.model, getConfigurationFieldKey("Status")) !== status;
}

function hideIfNotChangeType(changeType: string) {
  return (_: any, __: any, scope: ITemplateScope) =>
    get(scope.model, getConfigurationFieldKey("ChangeType")) !== changeType;
}

function watchAndSetUndefinedIfNotStatus(
  status: string,
  field: string | string[]
): IWatcher {
  return {
    expression: (_, scope) => scope.model?.Configuration.Status !== status,
    listener: blankFieldListener(field),
  };
}

function blankFieldListener(
  field: string | string[]
): (
  field: IFieldRuntimeObject,
  newValue: any,
  oldValue: any,
  scope: ITemplateScope,
  stopWatching: Function
) => void {
  const fields = isArray(field) ? <string[]>field : [<string>field];
  return (_, newValue, ___, scope) => {
    if (newValue) {
      fields.forEach((f) => set(scope.model ?? {}, f, undefined));
    }
  };
}

function getConfigurationFieldKey(fieldName: string) {
  return `Configuration.${fieldName}`;
}

function getMachineFieldKey(fieldName: string) {
  return `Machine.${fieldName}`;
}
function getTypeFieldKey(fieldName: string) {
  return `Type.${fieldName}`;
}
